import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const REFRESH_INTERVAL_MS = 60_000; // 60s

function formatTimeAgo(iso) {
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch {
    return "";
  }
}

export const NotificationBell = () => {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/notificacoes/me", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      // ignore — endpoint pode estar indisponível durante deploy
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notificacoes/me/unread-count");
      setUnread(data.unread_count || 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const handleClick = async (n) => {
    if (!n.lida) {
      try {
        await api.post(`/notificacoes/${n.id}/lida`);
        setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, lida: true } : x)));
        setUnread((c) => Math.max(0, c - 1));
      } catch {
        /* silent */
      }
    }
    if (n.link) {
      setOpen(false);
      nav(n.link);
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notificacoes/marcar-todas");
      setItems((arr) => arr.map((x) => ({ ...x, lida: true })));
      setUnread(0);
    } catch {
      /* silent */
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
          aria-label="Notificações"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[380px] p-0"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <div className="text-sm font-semibold">Notificações</div>
            <div className="text-[11px] text-muted-foreground">
              {unread > 0 ? `${unread} não lida${unread > 1 ? "s" : ""}` : "Tudo em dia"}
            </div>
          </div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAll}
              className="h-7 px-2 text-xs"
              data-testid="notification-mark-all"
            >
              <CheckCheck size={14} className="mr-1" />
              Marcar todas
            </Button>
          )}
        </div>

        <div className="max-h-[440px] overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-muted-foreground">
              Carregando…
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-muted-foreground">
              Sem notificações por enquanto.
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left flex gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-100 transition-colors ${
                  n.lida ? "opacity-70" : "bg-orange-50/40"
                }`}
              >
                <div className="mt-0.5">
                  {!n.lida ? (
                    <span className="block h-2 w-2 rounded-full bg-orange-500" />
                  ) : (
                    <Check size={12} className="text-slate-400" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{n.titulo}</div>
                  <div className="text-xs text-slate-600 line-clamp-2">{n.mensagem}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                    {formatTimeAgo(n.criado_em)}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
